import {AutoLinkNode, LinkNode} from '@lexical/link';
import {DOMExportOutputMap, LexicalEditor, LexicalNode, TextNode} from 'lexical';
import {
  $isBeautifulMentionNode,
  BeautifulMentionComponentProps,
  BeautifulMentionsMenuItemProps,
  createBeautifulMentionNode,
} from 'lexical-beautiful-mentions';
import {
  CustomMenuItem,
  FormControl,
  FormField,
  LabelActions,
  StyledContentEditable,
  TextEditorContentProps,
  TextEditorReadyHandler,
  Tooltip,
} from 'platform/components';
import {Integer} from 'platform/foundation';
import {css, styled} from 'styled-components';

import {forwardRef} from 'react';
import {Path} from 'react-hook-form';

import {compose, isNil, toLower, prop, sortBy} from 'ramda';

import {TemplateContextEnum, useListContextParametersQuery} from '@dms/api/core';
import i18n from '@dms/i18n';
import {VARIABLE_REGEX} from '@dms/shared';

import {Nullish} from 'shared/web';

import {FormValues} from '../types/FormValues';

const PLACEHOLDER_SPLIT_REGEX = /(\{\{\s*[\w.]+\s*\}\})/g;

interface TemplateBodyFieldProps extends TextEditorContentProps {
  control: FormControl<FormValues>;
  name: Path<FormValues>;
  context: TemplateContextEnum | Nullish;
  label: string;
  isDisabled?: boolean;
  minHeight?: Integer;
  onEditorReady?: TextEditorReadyHandler;
  labelActions?: LabelActions;
}

const ContentEditableComponent = styled(StyledContentEditable)<{
  $isMessage: boolean;
  $isDisabled?: boolean;
}>`
  ${({$isDisabled}) =>
    $isDisabled &&
    css`
      pointer-events: none;
    `}

  .mentions {
    display: inline-block;
    background-color: ${({theme}) => theme.colors.palettes.blue[30][100]};
    border-radius: ${({theme}) => theme.radii.large};
    padding: ${({theme}) => theme.getSize(1)} ${({theme}) => theme.getSize(2)};
    transition: all 250ms;

    &:hover {
      background-color: ${({theme}) => theme.colors.palettes.blue[40][100]};
    }
  }
`;

// This is needed because Tooltip component wrap itself into <div>
// eslint-disable-next-line eag/no-css-property -- Muted pre-existing violation
const CustomMentionWrapper = styled.span`
  & > div {
    display: inline-block;
  }
`;

interface MenuItemComponentProps extends BeautifulMentionsMenuItemProps {
  ref?: React.Ref<HTMLLIElement>;
}

const MenuItemComponent = ({selected, ref, item, ...props}: MenuItemComponentProps) => {
  const component = <CustomMenuItem ref={ref} item={item} selected={selected} {...props} />;

  if (selected) {
    return (
      <Tooltip label={item.data?.description} isOpen placement="right" offset={14}>
        {component}
      </Tooltip>
    );
  }

  return component;
};

const CustomBeautifulMentionComponent = forwardRef<
  HTMLSpanElement,
  BeautifulMentionComponentProps<{id: string; description: string}>
>(({data, value, ...other}, ref) => (
  <CustomMentionWrapper ref={ref} {...other}>
    <Tooltip label={data?.description} placement="top">
      {value}
    </Tooltip>
  </CustomMentionWrapper>
));

const linkFormatter = (_editor: LexicalEditor, node: LexicalNode) => {
  const convertedNode = node as AutoLinkNode;
  const element = document.createTextNode(convertedNode.getURL());
  return {element};
};

const exportHtmlNodeTransforms = (nodes: DOMExportOutputMap) => {
  nodes.set(CustomMention[0], (_editor, node) => {
    const data = $isBeautifulMentionNode(node) ? node.getData() : undefined;
    const literal = data?.literal;
    // A placeholder loaded from the template keeps its original bytes; a new one is canonical.
    const element = document.createTextNode(
      typeof literal === 'string' ? literal : `{{ ${data?.code} }}`
    );
    return {element};
  });
  nodes.set(AutoLinkNode, linkFormatter);
  nodes.set(LinkNode, linkFormatter);

  return nodes;
};

const CustomMention = createBeautifulMentionNode(CustomBeautifulMentionComponent);

export function TemplateBodyField(props: TemplateBodyFieldProps) {
  const {data, isLoading} = useListContextParametersQuery(
    {
      context: props.context as TemplateContextEnum,
    },
    {
      skip: isNil(props.context),
    }
  );

  const contextParameters = sortBy(
    compose(toLower, prop('value')),
    data?.map((param) => ({
      value: param.label,
      id: param.code,
      ...param,
    })) || []
  );

  const transformStringToNodes = (value: string | null) => {
    if (!value) {
      return [];
    }
    return value
      .split(PLACEHOLDER_SPLIT_REGEX)
      .filter((part) => part.length > 0)
      .map((part) => {
        const code = part.match(VARIABLE_REGEX)?.[1];
        const contextParameter = isNil(code)
          ? undefined
          : contextParameters.find((param) => param.code === code);
        // An unknown placeholder stays literal text so it survives the editor round trip.
        return isNil(contextParameter)
          ? new TextNode(part)
          : new CustomMention[0]('/', contextParameter.label, {...contextParameter, literal: part});
      });
  };

  return (
    <FormField
      name={props.name}
      type="suggestionInput"
      control={props.control}
      key={props.name}
      label={props.label}
      ContentEditableComponent={ContentEditableComponent}
      isLoadingOptions={isLoading}
      minHeight={props.minHeight}
      isDisabled={props.isDisabled}
      prefix="/"
      options={contextParameters}
      MenuItemComponent={MenuItemComponent}
      exportHtmlNodeTransforms={exportHtmlNodeTransforms}
      transformStringToNodes={transformStringToNodes}
      CustomMention={CustomMention}
      onEditorReady={props.onEditorReady}
      editorContent={props.editorContent}
      selectionContent={props.selectionContent}
      editorHighlights={props.editorHighlights}
      onKeyDownCapture={props.onKeyDownCapture}
      labelActions={props.labelActions}
      placeholder={i18n.t('page.messageTemplates.labels.placeholderHint')}
    />
  );
}
